import { atsApiQuery } from '..';
import { buildListTemplatesFilterBy } from '../listTemplatesFilters';
import {
  appendSourceActionId,
  getAssessmentFormData,
  getEmailFormData,
  getInternalEmailFormData,
  getSurveyFormData,
} from './utils';
import { getEsignFormData } from './esignFormData';

export const interviewPlanApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getInterviewPlans: builder.query({
        providesTags: ['GetInterviewPlans'],
        query: ({ filters, search, sort, page = 1 } = {}) => {
          if (search) {
            const params = {
              filter_by: {},
              sort_by: {},
              page,
            };
            params['filter_by']['ats_job_name'] = search;
            params['sort_by'][sort?.element] = sort?.order;
            return {
              method: 'get',
              url: `/ats_jobs/list_templates?entity_scope_kind=ats_job_stage`,
              params,
            };
          }

          const params = {
            filter_by: {},
            sort_by: {},
            page,
          };
          params['sort_by'][sort?.element] = sort?.order;
          params['filter_by'] = buildListTemplatesFilterBy(filters);

          return {
            method: 'get',
            url: `/ats_jobs/list_templates?entity_scope_kind=ats_job_stage`,
            params,
          };
        },
      }),

      getInterviewPlanById: builder.query({
        transformResponse: (response) => response?.data,
        providesTags: ['GetInterviewPlanById'],
        query: ({ atsJobId }) => {
          return {
            method: 'get',
            url: `/ats_jobs/${atsJobId}/show_template?entity_scope_kind=ats_job_stage`,
          };
        },
      }),

      createInterviewPlan: builder.mutation({
        transformResponse: (response) => response.data,
        query: (payload = {}) => {
          return {
            method: 'post',
            url: `/ats_jobs/create_template`,
            data: payload,
          };
        },
        invalidatesTags: ['GetInterviewPlans'],
      }),

      archiveInterviewPlan: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ atsJobId }) => {
          return {
            method: 'delete',
            url: `/ats_jobs/${atsJobId}/archive_template`,
          };
        },
        invalidatesTags: ['GetInterviewPlans'],
      }),

      cloneInterviewPlan: builder.mutation({
        transformResponse: (response) => response?.data,
        query: (payload = {}) => {
          const { atsJobId, params = {} } = payload;
          return {
            method: 'post',
            url: `/ats_jobs/${atsJobId}/clone`,
            params: params,
          };
        },
        invalidatesTags: ['GetInterviewPlans'],
      }),

      updateInterviewPlanById: builder.mutation({
        transformResponse: (response) => response.data,
        invalidatesTags: ['GetInterviewPlanById'],
        query: ({ atsJobId, payload = {} }) => {
          return {
            method: 'put',
            url: `/ats_jobs/${atsJobId}/update_template`,
            data: payload,
          };
        },
      }),

      getInterviewStagesById: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ atsJobId }) => {
          return {
            method: 'get',
            url: `/ats_jobs/${atsJobId}/ats_job_stages`,
          };
        },
      }),

      createInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages`,
            method: 'post',
            data: { ...params },
          };
        },
      }),
      updateInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, params, stageId } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}`,
            method: 'put',
            data: { ...params },
          };
        },
      }),
      deleteInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, ...params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}`,
            method: 'delete',
            data: { ...params },
          };
        },
      }),
      hasCandidatesInInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/has_candidates`,
            method: 'get',
          };
        },
      }),
      hasReferencesInInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/has_references`,
            method: 'get',
          };
        },
      }),
      rearrangeInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/rearrange`,
            method: 'put',
            data: { ...params },
          };
        },
      }),
      importInterviewStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/copy_stages`,
            method: 'post',
            data: { ...params },
          };
        },
      }),
      createActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: { ...params },
          };
        },
      }),
      createEmailActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          const fd = new FormData();
          const formData = appendSourceActionId(
            getEmailFormData(fd, params),
            params
          );
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: formData,
          };
        },
      }),
      createSurveyActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          const fd = new FormData();
          const formData = appendSourceActionId(
            getSurveyFormData(fd, params),
            params
          );
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: formData,
          };
        },
      }),
      createInternalEmailActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          const fd = new FormData();
          const formData = appendSourceActionId(
            getInternalEmailFormData(fd, params),
            params
          );
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: formData,
          };
        },
      }),
      updateInternalEmailActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params, activityId } = payload;
          const fd = new FormData();
          const formData = getInternalEmailFormData(fd, params);
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: formData,
          };
        },
      }),
      updateEmailActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params, activityId } = payload;
          const fd = new FormData();
          const formData = getEmailFormData(fd, params);
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: formData,
          };
        },
      }),
      updateSurveyActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params, activityId } = payload;
          const fd = new FormData();
          const formData = getSurveyFormData(fd, params);
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: formData,
          };
        },
      }),
      createAssessmentActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          const fd = new FormData();
          const formData = appendSourceActionId(
            getAssessmentFormData(fd, params),
            params
          );
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: formData,
          };
        },
      }),
      updateAssessmentActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params, activityId } = payload;
          const fd = new FormData();
          const formData = getAssessmentFormData(fd, params);
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: formData,
          };
        },
      }),
      getActivity: builder.query({
        transformResponse: (response) => response?.data,
        query: (params) => {
          const { jobId, stageId, activityId } = params;
          return {
            method: 'get',
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
          };
        },
        keepUnusedDataFor: 0,
      }),
      updateActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, activityId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: { ...params },
          };
        },
      }),
      deleteActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, activityId } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}/delete`,
            method: 'put',
          };
        },
      }),
      rearrangeActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, activityId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}/rearrange`,
            method: 'put',
            data: { ...params },
          };
        },
      }),
      // --- DocuSign stage activity (flag: t_docusign_on_all_stages) ---
      // Multipart, because the activity carries its own uploaded .docx files.
      createEsignActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions`,
            method: 'post',
            data: appendSourceActionId(
              getEsignFormData(params, { isCreate: true }),
              params
            ),
          };
        },
      }),
      updateEsignActivity: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, activityId, params } = payload;
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/${activityId}`,
            method: 'put',
            data: getEsignFormData(params),
          };
        },
      }),
      // Stateless per-file token check for the wizard — persists nothing.
      validateEsignDocuments: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          const { jobId, stageId, files = [] } = payload;
          const formData = new FormData();
          files.forEach((file) => {
            formData.append('esign_documents[]', file);
          });
          return {
            url: `/ats_jobs/${jobId}/ats_job_stages/${stageId}/ats_job_stage_actions/validate_esign_documents`,
            method: 'post',
            data: formData,
          };
        },
      }),
      getDefaultScorcard: builder.query({
        transformResponse: (response) => response.data,
        query: ({ payload = {} }) => {
          return {
            url: `/ats_scorecards/default_template`,
            method: 'get',
            params: payload,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useCreateInterviewStageMutation,
  useUpdateInterviewStageMutation,
  useDeleteInterviewStageMutation,
  useHasCandidatesInInterviewStageMutation,
  useHasReferencesInInterviewStageMutation,
  useRearrangeInterviewStageMutation,
  useImportInterviewStageMutation,
  useCreateActivityMutation,
  useGetActivityQuery,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useRearrangeActivityMutation,
  useCreateInterviewPlanMutation,
  useGetInterviewPlanByIdQuery,
  useGetInterviewStagesByIdQuery,
  useUpdateInterviewPlanByIdMutation,
  useGetInterviewPlansQuery,
  useCloneInterviewPlanMutation,
  useArchiveInterviewPlanMutation,
  useCreateEmailActivityMutation,
  useUpdateEmailActivityMutation,
  useGetDefaultScorcardQuery,
  useCreateAssessmentActivityMutation,
  useUpdateAssessmentActivityMutation,
  useCreateInternalEmailActivityMutation,
  useUpdateInternalEmailActivityMutation,
  useCreateSurveyActivityMutation,
  useUpdateSurveyActivityMutation,
  useCreateEsignActivityMutation,
  useUpdateEsignActivityMutation,
  useValidateEsignDocumentsMutation,
} = interviewPlanApi;
